import { cloneDeep, merge } from 'lodash'
import { useEffect } from 'react'

import useGetOrPostChatbotSettings from '~/features/agents/hooks/useGetOrPostChatbotSettings'
import useUpdateChatbotSettings from '~/features/agents/hooks/useUpdateChatbotSettings'
import useUserAccess from '~/features/auth/hooks/useUserAccess'
import { defaultChatBotLayout } from '~/features/settings/components/chatBot/utils'
import { EChatbotTheme } from '~/features/settings/models'
import { sessionStore } from '~/store'

/**
 * Reset chatbot settings for users who are not on a paid plan
 * This scenario is for users who have custom chatbot settings and are not on a paid plan
 * Use Case: A user has a subscription expired and hence they are not on a paid plan, or they have downgraded their plan
 */

const useResetChatbotSettings = () => {
  const layoutData = useGetOrPostChatbotSettings()

  const { isPlusUser, isBasicUser, isBasicFreeTrial } = useUserAccess()

  const isPaidUser = isPlusUser || isBasicFreeTrial || isBasicUser

  const { mutate: updateChatbotMutation } = useUpdateChatbotSettings()

  const agentId = sessionStore((state) => state.agent.id) || ''

  useEffect(() => {
    const isCustomLinkView =
      layoutData?.selection.custom?.[EChatbotTheme.linkView] ?? false
    const isCustomSimple =
      layoutData?.selection.custom?.[EChatbotTheme.simple] ?? false

    if (!isPaidUser && (isCustomLinkView || isCustomSimple)) {
      const resetLayout = merge({}, defaultChatBotLayout, {
        selection: {
          theme: layoutData?.selection.theme,
          [EChatbotTheme.simple]: isCustomSimple
            ? defaultChatBotLayout.selection[EChatbotTheme.simple]
            : layoutData?.selection[EChatbotTheme.simple],
          [EChatbotTheme.linkView]:
            layoutData?.selection[EChatbotTheme.linkView],
        },
        ...(isCustomLinkView && {
          options: cloneDeep(defaultChatBotLayout.options),
        }),
      })

      updateChatbotMutation({
        agentId,
        layout: resetLayout,
      })
    }
  }, [isPaidUser, agentId, updateChatbotMutation, layoutData?.selection])
}

export default useResetChatbotSettings
